import { useEffect, useState } from "react";
import { useI18n } from "../i18n";
import { linkMember, loadDirectory, loadMembers, loadRoles, setMemberActive, setMemberRole } from "../data";
import type { DirectoryRow, Member, Workspace } from "../types";
import { Invites } from "../components/Invites";

export function Members({ ws }: { ws: Workspace }) {
  const { t, pick } = useI18n();
  const [members, setMembers] = useState<Member[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [roles, setRoles] = useState<{ key: string; name_en: string; name_ar: string }[]>([]);
  const [saved, setSaved] = useState<string | null>(null);
  const canManage = ws.permissions.has("members.manage");
  const canLink = ws.permissions.has("members.manage") && ws.permissions.has("employees.read");

  const refresh = () =>
    loadMembers(ws.membership.company_id)
      .then(setMembers)
      .catch((err) => setError(err instanceof Error ? err.message : t("errorGeneric")));

  async function link(m: Member, employmentId: string) {
    setError(null);
    try {
      await linkMember(m.id, employmentId || null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    }
  }

  async function change(fn: () => Promise<void>, done: string) {
    setError(null);
    setSaved(null);
    try {
      await fn();
      await refresh();
      setSaved(done);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errorGeneric"));
    }
  }

  useEffect(() => {
    void refresh();
    if (canManage) void loadRoles(ws.membership.company_id).then(setRoles).catch(() => setRoles([]));
    if (canLink) void loadDirectory(ws.membership.company_id).then((all) => setPeople(all.filter((p) => p.status !== "terminated")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.membership.company_id]);

  return (
    <div className="page">
      {ws.permissions.has("members.manage") && <Invites ws={ws} />}
      <section className="card">
        <h2>{t("members")}</h2>
        {canLink && <p className="muted">{t("linkHelp")}</p>}
        {canManage && <p className="muted small-note">{t("roleHelp")}</p>}
        {error && <p className="alert danger" role="alert">{error}</p>}
        {saved && <p className="alert info" role="status">{saved}</p>}
        {members === null ? (
          <p className="muted">{t("loading")}</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t("fullName")}</th>
                  <th>{t("role")}</th>
                  <th>{t("status")}</th>
                  {canLink && <th>{t("linkedEmployee")}</th>}
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id}>
                    <td>{m.display_name ?? "—"}</td>
                    <td>
                      {canManage && m.id !== ws.membership.id ? (
                        <select aria-label={`${t("role")}: ${m.display_name ?? ""}`} value={m.role.key}
                          onChange={(e) => { const r = roles.find((x) => x.key === e.target.value); void change(() => setMemberRole(m.id, e.target.value), t("roleChanged").replace("{name}", m.display_name ?? "").replace("{role}", r ? pick(r.name_en, r.name_ar) : e.target.value)); }}>
                          {roles.map((r) => <option key={r.key} value={r.key}>{pick(r.name_en, r.name_ar)}</option>)}
                        </select>
                      ) : (
                        <>{pick(m.role.name_en, m.role.name_ar)}{m.id === ws.membership.id && <span className="muted small-note"> · {t("you")}</span>}</>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${m.active ? "success" : "neutral"}`}>{m.active ? t("active") : t("inactive")}</span>
                      {canManage && m.id !== ws.membership.id && (
                        <> <button type="button" className="link" onClick={() => {
                          if (m.active && !window.confirm(t("accessOffConfirm"))) return;
                          void change(() => setMemberActive(m.id, !m.active), m.active ? t("accessOff") : t("accessOn"));
                        }}>{m.active ? t("switchOff") : t("switchOn")}</button></>
                      )}
                    </td>
                    {canLink && (
                      <td>
                        <select id={`link-${m.id}`} aria-label={t("linkedEmployee")} value={m.employment_id ?? ""} onChange={(e) => void link(m, e.target.value)}>
                          <option value="">{t("notLinked")}</option>
                          {people.map((p) => <option key={p.employment_id} value={p.employment_id}>{pick(p.full_name, p.full_name_ar)} · {p.employee_number}</option>)}
                        </select>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
